import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Calendar, Users, Bot, ArrowRight, CheckCircle, Star, Euro, GraduationCap, Sparkles, X, MessageCircle, Phone, Globe } from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import SEO from '../components/SEO';
import WordOfDay from '../components/WordOfDay';
import { motion, AnimatePresence } from 'framer-motion';
import { apiGet, apiPost } from '../utils/api';

const COUNTRIES = [
  {
    id: 'italy',
    name: 'Italy',
    tagline: 'La Dolce Vita',
    description: 'Renaissance art, world-class cuisine, 20 unique regions from Alpine peaks to Mediterranean shores.',
    regions: '20 Regions',
    image: 'https://images.unsplash.com/photo-1523906834658-6e24ef2386f9?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
    flag: '\ud83c\uddee\ud83c\uddf9',
    color: 'from-green-700 to-green-900'
  },
  {
    id: 'spain',
    name: 'Spain',
    tagline: 'Viva Espana',
    description: 'Flamenco, tapas, sunny beaches, and vibrant cities. 17 autonomous communities to explore.',
    regions: '17 Communities',
    image: 'https://images.unsplash.com/photo-1583422409516-2895a77efded?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
    flag: '\ud83c\uddea\ud83c\uddf8',
    color: 'from-red-700 to-amber-700'
  },
  {
    id: 'portugal',
    name: 'Portugal',
    tagline: 'Saudade & Sun',
    description: 'Atlantic coastline, Fado music, azulejo tiles, and one of Europe\'s best-kept secrets.',
    regions: '8 Regions',
    image: 'https://images.unsplash.com/photo-1585208798174-6cedd86e019a?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
    flag: '\ud83c\uddf5\ud83c\uddf9',
    color: 'from-green-800 to-red-800'
  }
];

const LandingPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [showPopup, setShowPopup] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const userData = await apiGet('/api/auth/me');
        setUser(userData);
      } catch (error) {
        console.log('Not authenticated');
      }
    };
    checkAuth();
  }, []);

  useEffect(() => {
    if (user) return;
    const dismissed = sessionStorage.getItem('popup_dismissed');
    if (dismissed) return;
    const timer = setTimeout(() => setShowPopup(true), 8000);
    return () => clearTimeout(timer);
  }, [user]);

  const handleLogout = async () => {
    try { await apiPost('/api/auth/logout', {}); } catch (e) {}
    setUser(null);
    navigate('/');
  };

  const handleCountrySelect = (countryId) => {
    localStorage.setItem('selectedCountry', countryId);
    navigate('/regions');
  };

  const features = [
    { icon: MapPin, title: 'Regional Guides', description: 'Comprehensive information on every region — visas, taxes, cost of living, and more.' },
    { icon: Calendar, title: 'Events & Festivals', description: 'Never miss a festival or national holiday. Stay connected to local culture.' },
    { icon: Users, title: 'Community Forum', description: 'Connect with expats and locals. Share experiences and get real advice.' },
    { icon: Bot, title: 'AI Assistant', description: 'Get instant answers about visas, taxes, and living abroad.' }
  ];

  const benefits = [
    'Legal requirements for each region',
    'Cost of living estimates',
    'Tax implications explained',
    'Visa advice and guidance',
    'Festival and holiday calendar',
    'Interactive community forum',
    'AI-powered chat assistant',
    'Regular content updates'
  ];

  return (
    <div className="min-h-screen">
      <Navbar user={user} onLogout={handleLogout} />
      <SEO path="/" />

      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1726009955672-eaf577c89972?crop=entropy&cs=srgb&fm=jpg&q=85)' }}
        />
        <div className="absolute inset-0 hero-overlay" />

        <div className="relative z-10 text-center px-4 sm:px-6 max-w-5xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm rounded-full px-4 py-2 mb-6"
          >
            <Globe size={16} className="text-white" />
            <span className="text-white/90 text-sm font-medium">Italy &middot; Spain &middot; Portugal</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl sm:text-5xl md:text-7xl font-bold tracking-tight leading-[0.95] text-white mb-6"
          >
            Your European<br />Adventure Starts Here
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-base sm:text-lg md:text-xl text-white/90 mb-8 leading-relaxed max-w-2xl mx-auto"
          >
            Everything you need to relocate to Southern Europe with confidence. Legal guidance, regional insights, and a supportive community.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Button
              onClick={() => navigate('/register')}
              className="bg-accent hover:bg-accent/90 text-white rounded-full px-8 py-6 text-lg transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1"
              data-testid="get-started-button"
            >
              Sign Up Free <ArrowRight className="ml-2" size={20} />
            </Button>
            <Button
              onClick={() => document.getElementById('country-select')?.scrollIntoView({ behavior: 'smooth' })}
              variant="outline"
              className="bg-white/90 hover:bg-white text-primary rounded-full px-8 py-6 text-lg border-none"
              data-testid="explore-regions-button"
            >
              Choose Your Country
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Country Selection */}
      <section id="country-select" className="py-16 md:py-24 px-4 sm:px-6 md:px-12 lg:px-24 bg-muted" data-testid="country-select-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-primary mb-4">
              Where Do You Want to Live?
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
              Choose your destination and explore comprehensive guides for every region
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {COUNTRIES.map((country, index) => (
              <motion.div
                key={country.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                className="group cursor-pointer"
                onClick={() => handleCountrySelect(country.id)}
                data-testid={`country-card-${country.id}`}
              >
                <div className="relative h-80 sm:h-96 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2">
                  <img
                    src={country.image}
                    alt={country.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    loading="lazy"
                  />
                  <div className={`absolute inset-0 bg-gradient-to-t ${country.color} opacity-60 group-hover:opacity-70 transition-opacity`} />
                  <div className="absolute inset-0 flex flex-col justify-end p-6 sm:p-8">
                    <span className="text-4xl mb-2">{country.flag}</span>
                    <h3 className="text-3xl sm:text-4xl font-bold text-white mb-1">{country.name}</h3>
                    <p className="text-white/80 text-sm font-medium italic mb-3">{country.tagline}</p>
                    <p className="text-white/70 text-sm leading-relaxed mb-4 line-clamp-2">{country.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-white/60 text-xs font-medium uppercase tracking-wider">{country.regions}</span>
                      <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center group-hover:bg-white/30 transition-colors">
                        <ArrowRight size={18} className="text-white" />
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-24 px-4 sm:px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-primary mb-4">
              Everything You Need in One Place
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
              Comprehensive resources for relocating to Italy, Spain, or Portugal
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white border border-border rounded-2xl p-6 md:p-8 hover:border-primary/30 transition-all duration-300 hover:shadow-lg"
              >
                <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-primary/10 flex items-center justify-center mb-4 md:mb-6">
                  <feature.icon size={24} className="text-primary" />
                </div>
                <h3 className="text-xl md:text-2xl font-medium text-primary mb-2 md:mb-3">{feature.title}</h3>
                <p className="text-sm md:text-base leading-relaxed text-muted-foreground">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 md:py-24 px-4 sm:px-6 md:px-12 lg:px-24 bg-muted">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            <div>
              <img
                src="https://images.unsplash.com/photo-1658780508578-bbef20eb36c0?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="European lifestyle"
                className="rounded-2xl shadow-xl w-full h-64 sm:h-80 md:h-[500px] object-cover"
              />
            </div>
            <div>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-primary mb-4 md:mb-6">
                Start Your European Adventure
              </h2>
              <p className="text-base sm:text-lg leading-relaxed text-muted-foreground mb-6 md:mb-8">
                From legal requirements to local festivals, we provide everything you need to settle into your new European life with ease and confidence.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 mb-6 md:mb-8">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <CheckCircle size={20} className="text-emerald-500 mt-1 flex-shrink-0" />
                    <span className="text-base text-primary">{benefit}</span>
                  </div>
                ))}
              </div>
              <Button
                onClick={() => navigate('/register')}
                className="bg-primary hover:bg-primary/90 text-white rounded-full px-8 py-6 text-lg"
                data-testid="view-pricing-button"
              >
                Sign Up Free <ArrowRight className="ml-2" size={20} />
              </Button>
            </div>
          </div>

          <div className="mt-12 md:mt-16 grid grid-cols-1 md:grid-cols-2 gap-6">
            <WordOfDay />
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white border border-border rounded-2xl p-6 sm:p-8 hover:border-primary/30 transition-all shadow-sm flex flex-col justify-between"
              data-testid="quick-links-widget"
            >
              <div>
                <h3 className="text-lg font-semibold text-primary mb-4">Quick Tools</h3>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { label: 'Cost Calculator', to: '/cost-calculator' },
                    { label: 'School Finder', to: '/school-finder' },
                    { label: 'Region Compare', to: '/compare' },
                    { label: 'Country Compare', to: '/country-compare' },
                    { label: 'Visa Guide', to: '/visa-guide' },
                    { label: 'School Reviews', to: '/school-reviews' },
                    { label: 'Chat Rooms', to: '/chatrooms' },
                  ].map(link => (
                    <Button
                      key={link.to}
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(link.to)}
                      className="rounded-full text-xs justify-start"
                    >
                      {link.label}
                    </Button>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 px-4 sm:px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-primary mb-4 md:mb-6">
            Ready to Move to Europe?
          </h2>
          <p className="text-base sm:text-lg leading-relaxed text-muted-foreground mb-6 md:mb-8">
            Join thousands already planning their European adventure. Completely free.
          </p>
          <Button
            onClick={() => navigate('/register')}
            className="bg-accent hover:bg-accent/90 text-white rounded-full px-8 sm:px-12 py-5 sm:py-7 text-lg sm:text-xl transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1"
            data-testid="subscribe-now-button"
          >
            Get Started Free
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-primary text-white py-8 md:py-12 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <p className="text-lg font-accent mb-1">MoveToEurope</p>
              <p className="text-sm text-white/70">Your trusted guide to European relocation</p>
            </div>
            <div className="flex items-center gap-6">
              <a href="https://wa.me/447907012666" target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
                data-testid="footer-whatsapp-link">
                <MessageCircle size={18} />
                <span className="text-sm">WhatsApp Support</span>
              </a>
              <a href="tel:+447907012666"
                className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
                data-testid="footer-phone-link">
                <Phone size={16} />
                <span className="text-sm">+44 7907 012666</span>
              </a>
            </div>
          </div>
          <p className="text-sm text-white/50 mt-6 text-center">
            &copy; 2026 MoveToEurope. In partnership with MJR Associates &mdash; Gateway to Europe. All rights reserved.
          </p>
        </div>
      </footer>

      {/* Floating WhatsApp Button */}
      <a href="https://wa.me/447907012666" target="_blank" rel="noopener noreferrer"
        className="fixed bottom-6 left-6 z-50 w-14 h-14 bg-[#25D366] rounded-full flex items-center justify-center shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-200"
        data-testid="whatsapp-floating-btn" title="Chat with us on WhatsApp">
        <svg viewBox="0 0 24 24" width="28" height="28" fill="white">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </a>

      {/* Lead Capture Popup */}
      <AnimatePresence>
        {showPopup && !user && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
            onClick={() => { setShowPopup(false); sessionStorage.setItem('popup_dismissed', '1'); }}
            data-testid="lead-capture-overlay"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="bg-white rounded-2xl p-6 sm:p-8 max-w-md w-full shadow-2xl relative"
              onClick={(e) => e.stopPropagation()}
              data-testid="lead-capture-popup"
            >
              <button onClick={() => { setShowPopup(false); sessionStorage.setItem('popup_dismissed', '1'); }}
                className="absolute top-4 right-4 text-muted-foreground hover:text-primary transition-colors"
                data-testid="popup-close-btn">
                <X size={20} />
              </button>
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <Globe size={28} className="text-primary" />
                </div>
                <h3 className="text-2xl font-bold text-primary mb-2">Explore 45 Regions</h3>
                <p className="text-muted-foreground text-sm mb-6 leading-relaxed">
                  Sign up for free to unlock detailed guides across Italy, Spain, and Portugal — visas, taxes, local food, schools, and more.
                </p>
                <Button onClick={() => { setShowPopup(false); navigate('/register'); }}
                  className="w-full bg-accent hover:bg-accent/90 text-white rounded-full py-5 text-base mb-3"
                  data-testid="popup-signup-btn">
                  Sign Up Free <ArrowRight className="ml-2" size={18} />
                </Button>
                <p className="text-xs text-muted-foreground">
                  Already have an account?{' '}
                  <button onClick={() => { setShowPopup(false); navigate('/login'); }}
                    className="text-primary font-medium hover:underline bg-transparent border-none cursor-pointer"
                    data-testid="popup-login-link">
                    Sign in
                  </button>
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LandingPage;
